import {
  ACTION_CARD_CLICKED,
  CATEGORY_WIDGET_BUSINESS_CARD,
} from 'utils/analytics/eventTypesConstants';
import { CardActionArea, CardContent } from 'ui';
import { CardContentDetails } from 'consumer-page-widgets/CardContentDetails';
import { CardImage } from './CardImage';
import { CardTitle } from './CardTitle';
import { Theme, css } from '@emotion/react';
import { TypographyProps } from 'omakase';
import { consumer } from '@tock/coretypes';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { find, isEmpty } from 'es-toolkit/compat';
import { useExperimentVariant } from 'hooks/useExperiment';
import Anchor from 'components/Anchor';
import Skeleton from '@material-ui/lab/Skeleton';

const cardRootCss = css`
  border-radius: 0;
  border-width: 0;
  height: 100%;
  overflow: visible;
`;

const cardActionAreaRootCss = (theme: Theme) => css`
  flex: 1;
  height: 100%;
  padding: 0;
  border: 1px solid ${theme.colors.borderDefault};
  border-bottom-width: 2px;
  position: relative;

  &,
  &::after {
    transition: ${theme.transitions.duration.short}ms border ${theme.transitions.easing.easeInOut};
  }
  // This is the very important full bleed border bottom.
  // It "removes" the weird native bevel corners you get with > 1px borders.
  &::after {
    border-bottom: 2px solid ${theme.colors.borderDefault};
    bottom: -2px;
    content: '';
    left: -1px;
    position: absolute;
    width: calc(100% + 2px);
  }
  &:hover {
    background-color: ${theme.colors.bgDefault};
    text-decoration: none !important;
    &::after {
      border-bottom-color: ${theme.colors.fgPrimaryBase};
    }
  }
  & h3 {
    transition: ${theme.transitions.duration.short}ms color ${theme.transitions.easing.easeInOut};
  }
  &:hover h3 {
    color: ${theme.colors.fgPrimaryBase};
    @media (hover: none) {
      color: ${theme.colors.fgDefault};
    }
  }
  &:focus:not(.Mui-focusVisible) {
    box-shadow: none;
  }
  &.Mui-focusVisible {
    box-shadow: '0 0 0 1px ${theme.colors.borderPrimaryBase}';
    &,
    &::after {
      border-color: ${theme.colors.fgPrimaryBase};
    }
    & .MuiCardActionArea-focusHighlight {
      opacity: 0;
    }
  }
  &:focus h3 {
    color: ${theme.colors.fgPrimaryBase};
  }
  &:focus .MuiCardActionArea-focusHighlight {
    box-shadow: none;
  }
  &:active h3 {
    color: ${theme.colors.fgPrimaryBase};
  }
  &:hover .MuiCardActionArea-focusHighlight {
    opacity: 0;
  }
`;

const useStylesSkeletonCard = makeStyles((theme: Theme) =>
  createStyles({
    skeletonCard: {
      height: 0,
      width: '100%',
      paddingBottom: theme.mediaRatios.threeTwo,
      borderRadius: 3,
    },
    background: {
      backgroundColor: theme.colors.bgLight,
    },
  })
);

export interface Props {
  altText: string;
  body?: string;
  className?: string;
  dominantColor: string;
  highlightedNote?: JSX.Element | string;
  href: string;
  image: string;
  showBody?: boolean;
  subtitle?: React.ReactNode;
  subtitleDetailed?: React.ReactNode;
  subtitleProps?: Optional<TypographyProps>;
  title: React.ReactNode;
  titleProps?: Optional<TypographyProps>;
  widgetModifiers?: consumer.WidgetModifier[];
  index?: number;
  onClick?: () => void;
}

export const BusinessCard: React.FunctionComponent<Props> = ({
  altText,
  body,
  className,
  dominantColor,
  highlightedNote,
  href,
  image,
  showBody,
  subtitle,
  subtitleDetailed,
  subtitleProps,
  title,
  titleProps,
  widgetModifiers,
  index,
  onClick,
}) => {
  const showNeighborhoodExperiment = useExperimentVariant('WidgetBusinessNeighborhood');

  const widgetModifiersKeyHasBreakpoints = (key: string) => {
    const modifier = find(widgetModifiers, { key });
    return modifier && !isEmpty(modifier.breakpoint);
  };

  const showReleasesInfo = widgetModifiersKeyHasBreakpoints('showReleasesInfo');
  const showDescription = widgetModifiersKeyHasBreakpoints('showDescription');
  const subTitleDisplayType = find(widgetModifiers, { key: 'subtitleDisplayType' });

  const onClickGA = () => {
    window.analytics.sendEvent(CATEGORY_WIDGET_BUSINESS_CARD, ACTION_CARD_CLICKED, title, index);
    onClick && onClick();
  };

  const showDetailedSubtitle =
    showNeighborhoodExperiment === 1 ||
    (subTitleDisplayType && subTitleDisplayType.value === 'neighborhood');
  return (
    <CardActionArea
      className={className}
      css={[cardRootCss, cardActionAreaRootCss]}
      component={Anchor}
      href={href}
      role="link"
      disableRipple
      onClick={onClickGA}
    >
      <CardImage image={image} altText={altText} dominantColor={dominantColor} />
      <CardContent>
        <CardTitle
          gutterBottom={showDescription || showBody || showReleasesInfo}
          title={title}
          titleProps={titleProps}
          subtitle={showDetailedSubtitle ? subtitleDetailed : subtitle}
          subtitleProps={subtitleProps}
        />
        <CardContentDetails
          body={body}
          highlightedNote={highlightedNote}
          showBody={showDescription || showBody}
          showReleasesInfo={showReleasesInfo}
        />
      </CardContent>
    </CardActionArea>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const BusinessCardSkeleton = (props: any) => {
  const classes = useStylesSkeletonCard();
  return (
    <div {...props}>
      <Skeleton
        className={`${classes.skeletonCard} ${classes.background}`}
        variant="rect"
        animation="wave"
      />
      <Skeleton className={classes.background} variant="text" animation="wave" />
      <Skeleton className={classes.background} variant="text" width="60%" animation="wave" />
      <Skeleton className={classes.background} variant="text" width="40%" animation="wave" />
    </div>
  );
};
